import React, { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { api } from '@/lib/api';
import { BadgeIndianRupee, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

export const PROMO_STATUS = {
    requested: ['New request', 'bg-sky-50 text-sky-700 border-sky-200'],
    request_rejected: ['Request declined', 'bg-rose-50 text-rose-700 border-rose-200'],
    request_approved: ['Approved · awaiting payment', 'bg-emerald-50 text-emerald-700 border-emerald-200'],
    awaiting_payment: ['Payment not completed', 'bg-slate-100 text-slate-600 border-slate-200'],
    pending_approval: ['Paid · final approval', 'bg-amber-50 text-amber-700 border-amber-200'],
    approved: ['Live / scheduled', 'bg-emerald-50 text-emerald-700 border-emerald-200'],
    rejected: ['Rejected', 'bg-rose-50 text-rose-700 border-rose-200'],
};
export const PromoStatus = ({ p }) => {
    const [label, cls] = p.status === 'approved' && !p.start_at ? ['Removed', 'bg-slate-100 text-slate-600 border-slate-200'] : PROMO_STATUS[p.status] || [p.status, ''];
    return <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full border ${cls}`} data-testid={`promo-status-${p.id}`}>{label}</span>;
};

const Num = ({ label, value, onChange, testid, w = 'w-28' }) => (
    <div className="space-y-1.5"><Label className="text-xs">{label}</Label><Input type="number" min="1" className={w} value={value} onChange={e => onChange(e.target.value)} data-testid={testid} /></div>
);

const PackagesEditor = ({ packages, onChange }) => {
    const upd = (i, k, v) => onChange(packages.map((p, j) => (j === i ? { ...p, [k]: v } : p)));
    return (
        <div className="mt-4 space-y-2" data-testid="promo-packages-editor">
            <div className="flex items-center justify-between"><Label className="text-xs">Packages (optional)</Label>
                <Button size="sm" variant="outline" onClick={() => onChange([...packages, { name: '', days: 7, price_inr: '' }])} data-testid="promo-package-add"><Plus className="h-3.5 w-3.5 mr-1" /> Add package</Button></div>
            {packages.length > 0 && <div className="flex gap-2 text-[11px] text-muted-foreground"><span className="w-44">Name</span><span className="w-24">Days</span><span className="w-28">Price ₹</span></div>}
            {packages.map((p, i) => (
                <div key={p.id || i} className="flex flex-wrap items-center gap-2" data-testid={`promo-package-row-${i}`}>
                    <Input placeholder="Name e.g. Weekly" className="w-44" value={p.name} onChange={e => upd(i, 'name', e.target.value)} data-testid={`promo-package-name-${i}`} />
                    <Input type="number" min="1" placeholder="Days" className="w-24" value={p.days} onChange={e => upd(i, 'days', e.target.value)} data-testid={`promo-package-days-${i}`} />
                    <Input type="number" min="1" placeholder="Price ₹" className="w-28" value={p.price_inr} onChange={e => upd(i, 'price_inr', e.target.value)} data-testid={`promo-package-price-${i}`} />
                    <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => onChange(packages.filter((_, j) => j !== i))} data-testid={`promo-package-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
            ))}
        </div>
    );
};

export const PromoSettingsCard = () => {
    const [s, setS] = useState(null);
    const [saving, setSaving] = useState(false);
    useEffect(() => { api.get('/promotions/admin/settings').then(r => setS(r.data)).catch(() => {}); }, []);
    if (!s) return null;
    const set = (k) => (v) => setS({ ...s, [k]: v });
    const save = async () => {
        setSaving(true);
        try {
            await api.put('/promotions/admin/settings', {
                enabled: s.enabled, price_per_day: Number(s.price_per_day), min_days: Number(s.min_days), max_days: Number(s.max_days),
                max_slots_per_day: Number(s.max_slots_per_day), packages: s.packages.map(p => ({ ...p, days: Number(p.days), price_inr: Number(p.price_inr) })),
            });
            toast.success('Promotion settings saved');
        } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
        finally { setSaving(false); }
    };
    return (
        <Card className="p-4 sm:p-5" data-testid="promo-settings-card">
            <div className="font-display font-semibold flex items-center gap-2"><BadgeIndianRupee className="h-4 w-4 text-[#1F9D8A]" /> Paid banner promotions</div>
            <p className="text-xs text-muted-foreground mt-0.5">Users send banner, dates & package → you approve (adjust date/banner if needed) → they pay → you give final approval to go live.</p>
            <div className="mt-3 flex flex-wrap items-end gap-4">
                <div className="flex items-center gap-2"><Switch checked={s.enabled} onCheckedChange={set('enabled')} data-testid="promo-enabled-switch" /><span className="text-sm">{s.enabled ? 'Open for requests' : 'Closed'}</span></div>
                <Num label="Price per day (₹)" value={s.price_per_day} onChange={set('price_per_day')} testid="promo-price-per-day-input" w="w-32" />
                <Num label="Min days" value={s.min_days} onChange={set('min_days')} testid="promo-min-days-input" w="w-24" />
                <Num label="Max days" value={s.max_days} onChange={set('max_days')} testid="promo-max-days-input" w="w-24" />
                <Num label="Max banners live per day" value={s.max_slots_per_day} onChange={set('max_slots_per_day')} testid="promo-slots-input" w="w-32" />
            </div>
            <PackagesEditor packages={s.packages || []} onChange={set('packages')} />
            <Button onClick={save} disabled={saving} className="mt-4 bg-[#0E2A47] hover:bg-[#0B223A]" data-testid="promo-settings-save">{saving ? 'Saving…' : 'Save settings'}</Button>
        </Card>
    );
};
